<template>
  <div class="floating-dock" :class="{ 'expanded': expanded }">
    <!-- Toggle button -->
    <v-btn
      fab
      color="primary"
      class="dock-toggle"
      @click="expanded = !expanded"
      :icon="expanded ? 'mdi-close' : 'mdi-plus'"
      size="large"
    ></v-btn>
    
    <!-- Action buttons -->
    <transition name="dock-buttons">
      <div v-if="expanded" class="dock-buttons">
        <v-btn
          fab
          color="secondary"
          class="dock-button"
          @click="$emit('add-group')"
          :disabled="!canAddGroups"
          size="large"
        >
          <v-icon>mdi-folder-plus</v-icon>
          <v-tooltip activator="parent" location="left">
            Add Group
          </v-tooltip>
        </v-btn>
        
        <v-btn
          fab
          color="primary"
          class="dock-button"
          @click="$emit('add-item')"
          :disabled="!canAddItems"
          size="large"
        >
          <v-icon>mdi-plus</v-icon>
          <v-tooltip activator="parent" location="left">
            Add Item
          </v-tooltip>
        </v-btn>
      </div>
    </transition>
    
    <!-- Print Settings button (always visible) -->
    <v-btn
      fab
      color="success"
      class="print-button"
      @click="$emit('toggle-print-settings')"
      size="large"
    >
      <v-icon>mdi-printer</v-icon>
      <v-tooltip activator="parent" location="left">
        Print Settings
      </v-tooltip>
    </v-btn>
  </div>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'FloatingDock',
  props: {
    canAddGroups: {
      type: Boolean,
      default: true
    },
    canAddItems: {
      type: Boolean,
      default: true
    }
  },
  emits: ['add-group', 'add-item', 'toggle-print-settings'],
  setup() {
    const expanded = ref(false)

    return {
      expanded
    }
  }
}
</script>

<style scoped>
.floating-dock {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 12px;
}

.dock-toggle {
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.dock-toggle:hover {
  transform: scale(1.1);
}

.expanded .dock-toggle {
  transform: rotate(45deg);
}

.dock-buttons {
  display: flex;
  flex-direction: column-reverse;
  gap: 12px;
  align-items: center;
}

.dock-button {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.dock-button:hover:not(:disabled) {
  transform: scale(1.05);
}

.dock-button:disabled {
  opacity: 0.5;
}

.print-button {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.print-button:hover {
  transform: scale(1.05);
}

/* Transition animations */
.dock-buttons-enter-active,
.dock-buttons-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.dock-buttons-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.dock-buttons-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.dock-button {
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Staggered animation for buttons */
.dock-button:nth-child(1) {
  transition-delay: 0.1s;
}

.dock-button:nth-child(2) {
  transition-delay: 0.05s;
}

/* Mobile adjustments */
@media screen and (max-width: 768px) {
  .floating-dock {
    bottom: 15px;
    right: 15px;
    gap: 10px;
  }
  
  .dock-toggle,
  .dock-button,
  .print-button {
    width: 48px !important;
    height: 48px !important;
    min-width: 48px !important;
  }
  
  .dock-buttons {
    gap: 10px;
  }
}

/* Hide during print */
@media print {
  .floating-dock {
    display: none !important;
  }
}

/* Hide when print controls are expanded on small screens */
@media screen and (max-width: 768px) {
  .floating-dock.hide-on-mobile {
    display: none;
  }
}
</style>